import { useState, useEffect, useMemo } from 'react';
import { Table } from 'semantic-ui-react';
import { usePortfolioContext } from '~/hooks';
import { offtakeRateUnit } from '~/utils';
import { indexRfisByPortfolioId } from '~/api/requestForInformation';
import * as portfolioApi from '~/api/portfolios';

import { DealSummaryInvestorData, InvestorActivityChecklist as InvestorActivityChecklistType } from '~/types/portfolio/PortfolioDealSummary';
import PortfolioBidRow from './PortfolioBidRow';
import AcceptPortfolioBidModal from './modals/AcceptPortfolioBids';
import DeclinePortfolioBidModal from './modals/DeclinePortfolioBids';
import '../styles.scss';

type SortColumn = 'investor' | 'totalAmount' | 'wdcAmount' | 'kwhAmount' | 'lastUpdated';
type SortDirection = 'asc' | 'desc';
type SemanticSortDirection = 'ascending' | 'descending';


interface PortfolioBidTableProps {
    showDeclined?: boolean;
}

const PortfolioBidTable = ({ showDeclined = true }: PortfolioBidTableProps) => {
    const { portfolio, portfolioDisplayValues } = usePortfolioContext();
    const { dealSummary } = portfolioDisplayValues;
    const { investorProjectMap } = dealSummary || {};
    const investorArray = investorProjectMap ? Object.values(investorProjectMap) : [];
    const [sortColumn, setSortColumn] = useState<SortColumn>('totalAmount');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const [rfiData, setRfiData] = useState<any[]>([]);
    const [hasSummaryDoc, setHasSummaryDoc] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        const fetchRfis = async () => {
            try {
                const rfis = await indexRfisByPortfolioId(portfolio.id);
                setRfiData(rfis);
            } catch (error) {
                console.error('Error fetching RFIs for portfolio:', error);
            }
        };

        fetchRfis();
    }, [portfolio.id]);

    useEffect(() => {
        if (!portfolio?.id) return;
        const portfolioId = portfolio.parentId ?? portfolio.id;
        portfolioApi.getPortfolioSummaryDownloadUrl(portfolioId)
            .then((data: { presignedUrl?: string }) => setHasSummaryDoc(!!data?.presignedUrl))
            .catch(() => setHasSummaryDoc(false));
    }, [portfolio?.id, portfolio?.parentId]);


    // console.log('investorProjectArray', investorArray);

    const filteredInvestorArray: any = useMemo(() => {
        const withProjects = investorArray.filter(
            (investor: any) => investor.projects && Object.keys(investor.projects).length > 0
        );
        if (showDeclined) return withProjects;
        return withProjects.filter((investor: any) => {
            const projects = Object.values(investor.projects ?? {});
            if (!projects.length) return true;
            return !projects.every((p: any) => p?.status === 'DECLINED_TO_BID' || p?.status === 'BID_REJECTED');
        });
    }, [investorArray, showDeclined]);

    // Modal state
    const [acceptInvestorId, setAcceptInvestorId] = useState('');
    const [declineInvestorId, setDeclineInvestorId] = useState('');

    // Activity checklist by company ID (from dealSummary.investors)
    const activityByCompanyId = useMemo(() => {
        const investors = dealSummary?.investors ?? [];
        const map: Record<number, InvestorActivityChecklistType | null> = {};
        investors.forEach((inv: any) => {
            if (inv.companyId != null) map[inv.companyId] = inv.activity ?? null;
        });
        return map;
    }, [dealSummary?.investors]);

    // Process RFI data by investor
    const getRfiDataForInvestor = (investorId: string) => {
        if (!rfiData || rfiData.length === 0) return [];

        return rfiData.map(rfi => {
            const investorResponse = rfi.responses?.find((response: any) => response.companyId === investorId);
            return {
                name: rfi.name,
                description: rfi.description,
                content: investorResponse?.content || null,
                snippet: investorResponse?.snippet || '',
                updatedAt: investorResponse?.updatedAt || null
            };
        });
    };

    // Helper function to get bid value for sorting
    const getBidValueForSorting = (investor: any, unit: 'TOTAL' | 'WDC' | 'KWH') => {
        const projectsArray = investor.projects ? Object.values(investor.projects) : [];
        let bidTotal = 0;
        let totalSizeKwdc = 0;
        let count = 0;

        projectsArray.forEach((project: any) => {
            const bid = project.projectBids[0];
            if (bid && bid.amount) {
                const projectSizeKwdc = project.projectArrays.reduce((sum: number, array: any) => sum + (array.sizeKwdc || 0), 0);
                totalSizeKwdc += projectSizeKwdc;
                count += 1;
                if (bid.unit === 'WDC') {
                    bidTotal += bid.amount * projectSizeKwdc * 1000;
                } else {
                    bidTotal += bid.amount;
                }
            }
        });

        if (count === 0) return 0;

        if (unit === 'TOTAL') {
            return bidTotal;
        } else {
            return bidTotal / totalSizeKwdc / 1000;
        }
    };

    // Helper function to get last updated date for sorting
    const getLastUpdatedForSorting = (investor: any) => {
        const projectsArray = investor.projects ? Object.values(investor.projects) : [];
        let lastUpdatedDate: Date | null = null;

        projectsArray.forEach((project: any) => {
            if (project.projectBids[0]) {
                const projectUpdatedAt = project.projectBids[0].updatedAt ? new Date(project.projectBids[0].updatedAt) : null;
                const projectCreatedAt = project.projectBids[0].createdAt ? new Date(project.projectBids[0].createdAt) : null;

                if (projectUpdatedAt && (!lastUpdatedDate || projectUpdatedAt > lastUpdatedDate)) {
                    lastUpdatedDate = projectUpdatedAt;
                }
                if (projectCreatedAt && (!lastUpdatedDate || projectCreatedAt > lastUpdatedDate)) {
                    lastUpdatedDate = projectCreatedAt;
                }
            }
        });

        return lastUpdatedDate || new Date(0);
    };

    // Sort the filtered data based on current sort column and direction
    const sortedData = useMemo(() => {
        const dataToSort = [...filteredInvestorArray];
        const isInvestorDeclined = (investor: any) => {
            const projects = investor?.projects ? Object.values(investor.projects) : [];
            if (!projects.length) return false;
            return projects.every((p: any) => p?.status === 'DECLINED_TO_BID' || p?.status === 'BID_REJECTED');
        };

        return dataToSort.sort((a, b) => {
            const aDeclined = isInvestorDeclined(a);
            const bDeclined = isInvestorDeclined(b);
            if (aDeclined !== bDeclined) return aDeclined ? 1 : -1;

            let aValue: any;
            let bValue: any;

            switch (sortColumn) {
                case 'investor':
                    aValue = (a.investor?.company?.name || 'Unknown Investor').toLowerCase();
                    bValue = (b.investor?.company?.name || 'Unknown Investor').toLowerCase();
                    break;
                case 'totalAmount':
                    aValue = getBidValueForSorting(a, 'TOTAL');
                    bValue = getBidValueForSorting(b, 'TOTAL');
                    break;
                case 'wdcAmount':
                    aValue = getBidValueForSorting(a, 'WDC');
                    bValue = getBidValueForSorting(b, 'WDC');
                    break;
                case 'kwhAmount':
                    aValue = getBidValueForSorting(a, 'KWH');
                    bValue = getBidValueForSorting(b, 'KWH');
                    break;
                case 'lastUpdated':
                    aValue = getLastUpdatedForSorting(a);
                    bValue = getLastUpdatedForSorting(b);
                    break;
                default:
                    return 0;
            }

            // Primary sort by the selected column
            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }

            // Secondary sort by investor name (ascending) when primary values are equal
            const aName = (a.investor?.company?.name || 'Unknown Investor').toLowerCase();
            const bName = (b.investor?.company?.name || 'Unknown Investor').toLowerCase();
            return aName.localeCompare(bName);
        });
    }, [filteredInvestorArray, sortColumn, sortDirection]);

    // Handle column header click
    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            // If clicking the same column, toggle direction
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // If clicking a different column, set new column and default to appropriate direction
            setSortColumn(column);
            if (column === 'investor') {
                setSortDirection('asc');
            } else {
                setSortDirection('desc');
            }
        }
    };

    // Convert sort direction to Semantic UI format
    const getSemanticSortDirection = (column: SortColumn): SemanticSortDirection | undefined => {
        if (sortColumn !== column) return undefined;
        return sortDirection === 'asc' ? 'ascending' : 'descending';
    };


    return (
        <div className="bid-table-container">
            <Table compact responsive sortable>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell
                            width={4}
                            sorted={getSemanticSortDirection('investor')}
                            onClick={() => handleSort('investor')}
                            style={{ cursor: 'pointer' }}
                        >
                            Investor
                        </Table.HeaderCell>

                        {portfolio.projects[0].pricingType === 'OFFTAKE' ? (
                            <Table.HeaderCell
                                width={2}
                                textAlign="right"
                                sorted={getSemanticSortDirection('kwhAmount')}
                                onClick={() => handleSort('kwhAmount')}
                                style={{ cursor: 'pointer' }}
                            >
                                Avg Bid ($/{offtakeRateUnit(portfolio.projects[0].contractType)})
                            </Table.HeaderCell>
                        ) : null}
                        {portfolio.projects[0].pricingType === 'ACQUISITION' || portfolio.projects[0].pricingType === 'DEV_FEE' ? (
                            <>
                                <Table.HeaderCell
                                    width={2}
                                    textAlign="right"
                                    sorted={getSemanticSortDirection('wdcAmount')}
                                    onClick={() => handleSort('wdcAmount')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Avg Bid ($/Wdc)
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    width={2}
                                    textAlign="right"
                                    sorted={getSemanticSortDirection('totalAmount')}
                                    onClick={() => handleSort('totalAmount')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Total Bid
                                </Table.HeaderCell>
                            </>
                        ) : null}
                        <Table.HeaderCell
                            width={2}
                            textAlign="right"
                            sorted={getSemanticSortDirection('lastUpdated')}
                            onClick={() => handleSort('lastUpdated')}
                            style={{ cursor: 'pointer' }}
                        >
                            Last updated
                        </Table.HeaderCell>
                        <Table.HeaderCell width={2} textAlign="right">
                            Actions
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {sortedData.map((investor: any, index: number) => (
                        <PortfolioBidRow
                            key={portfolio.portfolioId || index}
                            portfolio={portfolio}
                            investorBidData={investor as DealSummaryInvestorData}
                            index={index}
                            setAcceptInvestorId={setAcceptInvestorId}
                            setDeclineInvestorId={setDeclineInvestorId}
                            rfiData={getRfiDataForInvestor(investor.investor?.company?.id)}
                            activity={activityByCompanyId[investor.investor?.company?.id]}
                            hasSummaryDoc={hasSummaryDoc}
                        />
                    ))}
                </Table.Body>
            </Table>

            {/* Modals */}
            {acceptInvestorId && (
                <AcceptPortfolioBidModal
                    investorId={acceptInvestorId}
                    closeModal={() => setAcceptInvestorId('')}
                />
            )}
            {declineInvestorId && (
                <DeclinePortfolioBidModal
                    investorId={declineInvestorId}
                    closeModal={() => setDeclineInvestorId('')}
                />
            )}
        </div>
    );
};

export default PortfolioBidTable;
